import React, { useEffect } from "react";
import CaseStudyLayout from "../CaseStudyLayout";

const FitnessByArtizen = () => {
  useEffect(() => {
    document.title = "Fitness by Artizen Case Study - Andy Persaud Portfolio";
    window.scrollTo(0, 0);
  }, []);

  return (
    <CaseStudyLayout>
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-purple-50 to-indigo-50 section-padding">
        <div className="container-narrow">
          <div className="text-center mb-12">
            <div className="mb-8">
              <img
                src="https://customer-assets.emergentagent.com/job_ux-design-revamp/artifacts/vjlao53w_PHOTO-2026-01-06-16-28-08.jpg"
                alt="Fitness by Artizen strength training environment"
                className="w-full max-w-4xl mx-auto rounded-2xl shadow-xl animate-fade-in object-cover object-center h-72 md:h-[360px]"
              />
            </div>

            <h1 className="heading-hero animate-slide-up text-gray-900 mb-4">Fitness by Artizen</h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed animate-slide-up">
              Designing a calm, premium, conversion-focused membership, trial, and consultation experience for a
              boutique strength club launch in NYC.
            </p>
          </div>

          {/* Project Overview */}
          <div className="grid md:grid-cols-4 gap-8 card-professional p-8 animate-scale-in">
            <div className="text-center">
              <h3 className="font-semibold text-gray-900 mb-2">Role</h3>
              <p className="text-gray-600">Lead UX Strategist &amp; Designer</p>
            </div>
            <div className="text-center">
              <h3 className="font-semibold text-gray-900 mb-2">Focus</h3>
              <p className="text-gray-600">Conversion Architecture &amp; Systems UX</p>
            </div>
            <div className="text-center">
              <h3 className="font-semibold text-gray-900 mb-2">Audience</h3>
              <p className="text-gray-600">Prospects, Members &amp; Admins</p>
            </div>
            <div className="text-center">
              <h3 className="font-semibold text-gray-900 mb-2">Engagement</h3>
              <p className="text-gray-600">0 → 1 Experience &amp; Operational Design</p>
            </div>
          </div>

          {/* Visit Live Website CTA */}
          <div className="text-center mt-8">
            <a
              href="https://fitness.artizennyc.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="interactive-button inline-flex items-center space-x-2 animate-fade-in"
            >
              <span>Visit Live Website</span>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                />
              </svg>
            </a>
          </div>
        </div>
      </section>

      {/* UX Goals & Success Criteria */}
      <section className="section-padding bg-white">
        <div className="container-narrow space-y-10">
          <div className="text-center mb-4">
            <h2 className="heading-section mb-4">UX Goals &amp; Success Criteria</h2>
            <p className="text-body max-w-3xl mx-auto">
              I approached Fitness by Artizen as a conversion architecture and systems design problem—balancing calm,
              premium brand expression with clear decision paths and lightweight, trustworthy flows.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="card-professional p-6 bg-slate-50 border border-slate-200">
              <h3 className="heading-subsection mb-3">Conversion Clarity</h3>
              <ul className="text-gray-700 text-sm space-y-2">
                <li>• Make Artizen instantly understandable above the fold.</li>
                <li>• Help users quickly see why it&apos;s different from typical gyms.</li>
                <li>• Support four clear ways to start: Open Gym, Classes, PF, PT.</li>
                <li>• PF/PT visitors know the next step is a low-pressure info call.</li>
              </ul>
            </div>

            <div className="card-professional p-6 bg-slate-50 border border-slate-200">
              <h3 className="heading-subsection mb-3">Reduced Friction</h3>
              <ul className="text-gray-700 text-sm space-y-2">
                <li>• Trials and consultations feel lightweight and approachable.</li>
                <li>• Forms only go deep after trust is established.</li>
                <li>• Embedded payments with clear order summary and legal clarity.</li>
                <li>• Post-purchase flows create momentum into booking.</li>
              </ul>
            </div>

            <div className="card-professional p-6 bg-slate-50 border border-slate-200">
              <h3 className="heading-subsection mb-3">Brand &amp; Operations</h3>
              <ul className="text-gray-700 text-sm space-y-2">
                <li>• Replace generic embeds with calm, branded UI patterns.</li>
                <li>• Maintain a low-stress, premium aesthetic end-to-end.</li>
                <li>• Give admin a clear picture of who reached out, for what, and when.</li>
                <li>• Separate sales opportunities from member support tasks.</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* The Problem */}
      <section className="section-padding bg-gray-50">
        <div className="container-narrow space-y-10">
          <h2 className="heading-section text-center mb-4">The Problem</h2>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Prospects & Members */}
            <div className="card-professional p-8 bg-white border border-slate-200">
              <h3 className="heading-subsection mb-3">Prospects &amp; Members</h3>
              <p className="text-body mb-4">
                People arriving at the site were often busy professionals who wanted a low-pressure gym experience, were
                curious about a calmer, premium strength club, but didn&apos;t want to be pushed into commitments.
              </p>
              <h4 className="font-semibold text-gray-900 mb-2">Key Friction Points</h4>
              <ul className="text-gray-700 text-sm space-y-2">
                <li>• No clear, confidence-building path from &quot;curious&quot; to &quot;get started.&quot;</li>
                <li>• Trial and consultation flows were fragmented across pages and tools.</li>
                <li>• Third-party scheduling/payment tools broke brand continuity.</li>
                <li>• Mobile interactions felt fragile: hidden nav, tap vs. expand confusion, scroll fatigue.</li>
              </ul>
            </div>

            {/* Business & Admin */}
            <div className="card-professional p-8 bg-white border border-slate-200">
              <h3 className="heading-subsection mb-3">Business &amp; Admin</h3>
              <p className="text-body mb-4">
                The owner needed more than a pretty landing page. They needed a way to see and act on real interest,
                especially for high-touch offers like Personalized Fitness and Personal Training.
              </p>
              <h4 className="font-semibold text-gray-900 mb-2">Operational Gaps</h4>
              <ul className="text-gray-700 text-sm space-y-2">
                <li>• No clean way to differentiate &quot;curious&quot; traffic from qualified interest.</li>
                <li>• No single view of who reached out, why, and when.</li>
                <li>• High-touch PF/PT offers needed human review before commitment.</li>
                <li>• Admin tasks for sales vs. member support were blended together.</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Process & UX Strategy */}
      <section className="section-padding bg-white">
        <div className="container-narrow space-y-10">
          <h2 className="heading-section text-center mb-6">Process &amp; UX Strategy</h2>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="card-professional p-6 bg-slate-50 border border-slate-200">
              <h3 className="heading-subsection mb-3">Conversion Architecture</h3>
              <p className="text-body text-sm">
                I treated the homepage and memberships experience as a decision system: clarify what Artizen is, then
                guide users into the right level of commitment.
              </p>
            </div>
            <div className="card-professional p-6 bg-slate-50 border border-slate-200">
              <h3 className="heading-subsection mb-3">Layered IA</h3>
              <p className="text-body text-sm">
                I separated low-, medium-, and high-commitment flows while designing both sides of the system: visitor
                UX and admin operational UX.
              </p>
            </div>
            <div className="card-professional p-6 bg-slate-50 border border-slate-200">
              <h3 className="heading-subsection mb-3">Calm, Premium UX</h3>
              <p className="text-body text-sm">
                I avoided &quot;nightclub gym&quot; and generic luxury UI, instead using a calm aesthetic that supports
                clarity, not hype.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Information Architecture */}
      <section className="section-padding bg-gray-50">
        <div className="container-narrow space-y-10">
          <h2 className="heading-section text-center mb-6">Information Architecture</h2>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="card-professional p-8 bg-white border border-slate-200">
              <h3 className="heading-subsection mb-3">Public Site</h3>
              <ul className="text-gray-700 text-sm space-y-2">
                <li>
                  <strong>Homepage:</strong> Positioning, differentiators, and &quot;4 ways to start&quot; as a first-decision
                  selector.
                </li>
                <li>
                  <strong>Memberships Page:</strong> Compare four offerings at a glance; pick how you want to train,
                  then choose a tier.
                </li>
                <li>
                  <strong>Dedicated Flow Pages:</strong> Tiered checkout for Open Gym and Classes; consultation-first
                  flows for PF/PT.
                </li>
              </ul>
            </div>

            <div className="card-professional p-8 bg-white border border-slate-200">
              <h3 className="heading-subsection mb-3">Admin Area</h3>
              <p className="text-body mb-3">
                I created a simple, task-oriented admin IA with clear separation between sales opportunities and
                member support.
              </p>
              <ul className="text-gray-700 text-sm space-y-2">
                <li>• Overview for high-level health.</li>
                <li>• Memberships for configuration.</li>
                <li>• Inquiries for PF/PT and membership interest.</li>
                <li>• Trial Requests as a dedicated queue.</li>
                <li>• Messages for general contact.</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Homepage & First-Decision UX */}
      <section className="section-padding bg-white">
        <div className="container-narrow space-y-10">
          <h2 className="heading-section text-center mb-6">Homepage &amp; First-Decision UX</h2>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="card-professional p-8 bg-slate-50 border border-slate-200">
              <h3 className="heading-subsection mb-3">Make Artizen Understandable in One Glance</h3>
              <ul className="text-gray-700 text-sm space-y-2">
                <li>• Direct headline: &quot;Train. Focus. Recover.&quot;</li>
                <li>• Microline: &quot;Memberships now open.&quot;</li>
                <li>
                  • Value prop: modern, low-stress strength club designed for clarity and performance—no
                  high-pressure sales.
                </li>
                <li>
                  • Two primary CTAs: &quot;View Memberships&quot; (primary) and &quot;Trial Pass&quot; (secondary) for a simple
                  choice.
                </li>
              </ul>
            </div>
            <div className="card-professional p-8 bg-slate-50 border border-slate-200">
              <h3 className="heading-subsection mb-3">Turn Abstract Benefits into Scannable Value</h3>
              <p className="text-body mb-3">
                The &quot;nervous system&quot; concept was compelling but abstract. I reframed it into practical, scannable
                benefits that users could repeat back.
              </p>
              <ul className="text-gray-700 text-sm space-y-2">
                <li>• Thoughtful Strength.</li>
                <li>• Soft Landings.</li>
                <li>• For Your Whole Day.</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Membership Decision Design */}
      <section className="section-padding bg-gray-50">
        <div className="container-narrow space-y-10">
          <h2 className="heading-section text-center mb-6">Membership Decision Design</h2>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="card-professional p-8 bg-white border border-slate-200">
              <h3 className="heading-subsection mb-3">The &quot;4-Card&quot; System</h3>
              <p className="text-body mb-3">
                The homepage uses four image-led cards as a first-impression selector: Open Gym, Classes, Personalized
                Fitness, and Personal Training.
              </p>
              <ul className="text-gray-700 text-sm space-y-2">
                <li>• Desktop: hover reveals more detail and &quot;Learn more.&quot;</li>
                <li>
                  • Mobile: tap-to-expand behavior with explicit &quot;Learn more&quot; CTA to solve tapping vs. navigation
                  confusion.
                </li>
                <li>
                  • Memberships page turns this into a comparison tool: first choose how you want to train, then a tier
                  inside that card.
                </li>
              </ul>
            </div>

            <div className="card-professional p-8 bg-white border border-slate-200">
              <h3 className="heading-subsection mb-3">Avoiding Decision Paralysis</h3>
              <p className="text-body mb-3">
                Instead of confronting users with a wall of plans and prices, I structured decisions into two steps:
              </p>
              <ul className="text-gray-700 text-sm space-y-2">
                <li>1) Pick how you want to train (Open Gym, Classes, PF, PT).</li>
                <li>2) Choose a tier inside that path.</li>
                <li>• Only one selection is active at a time to reduce confusion.</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Trials & Checkout */}
      <section className="section-padding bg-white">
        <div className="container-narrow space-y-10">
          <h2 className="heading-section text-center mb-6">Trials, Checkout &amp; Scheduling</h2>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Trials */}
            <div className="card-professional p-8 bg-slate-50 border border-slate-200">
              <h3 className="heading-subsection mb-3">Trials: Lightweight, Clear Completion</h3>
              <p className="text-body mb-3">
                Trial sign-up was originally spread across multiple pages and tools, with users unsure if they had
                actually completed registration.
              </p>
              <ul className="text-gray-700 text-sm space-y-2">
                <li>• Two-part pattern: short trial form + dedicated scheduler page.</li>
                <li>• Scheduler page makes it obvious that choosing a slot completes registration.</li>
                <li>• Trial options consolidated so the form matches real intent.</li>
              </ul>
            </div>

            {/* Checkout */}
            <div className="card-professional p-8 bg-slate-50 border border-slate-200">
              <h3 className="heading-subsection mb-3">Checkout: Guided, Trustworthy Purchase</h3>
              <p className="text-body mb-3">
                For Open Gym and Classes, I implemented a four-step checkout with a stepper that balances guidance and
                control.
              </p>
              <ul className="text-gray-700 text-sm space-y-2">
                <li>• Steps: Add-ons → Your Info → Review &amp; Payment → Welcome.</li>
                <li>• Stepper is clickable only for the current and previous steps.</li>
                <li>• Review &amp; Payment shows a clear order summary and personalized waiver.</li>
                <li>• Embedded payments keep users on-site and in-brand.</li>
              </ul>
            </div>
          </div>

          {/* Scheduling */}
          <div className="card-professional p-8 bg-gray-50 border border-slate-200">
            <h3 className="heading-subsection mb-3">Scheduling: Keep Users In-Flow</h3>
            <p className="text-body mb-3">
              Scheduling is a common drop-off point. I moved toward native-feeling scheduling patterns that preserve the
              calm brand while making availability obvious.
            </p>
            <ul className="text-gray-700 text-sm space-y-2">
              <li>• Branded calendar UI with dark/gold aesthetic.</li>
              <li>• Progressive disclosure: pick a date, then see the time grid.</li>
              <li>• Clear availability: highlight available days, disable unavailable ones.</li>
              <li>
                • Contextual scheduling: Open Gym members see gym time slots; Fitness Class members see classes; PF/PT
                see info call bookings.
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* PF/PT Consultation-First Flows */}
      <section className="section-padding bg-gray-50">
        <div className="container-narrow space-y-10">
          <h2 className="heading-section text-center mb-6">Personalized Fitness &amp; PT: Consultation-First</h2>

          <div className="card-professional p-8 bg-white border border-slate-200 space-y-6">
            <h3 className="heading-subsection mb-3">From Intimidating Forms to Low-Pressure Info Calls</h3>
            <p className="text-body mb-3">
              PF/PT prospects needed a confidence-building path from &quot;curious&quot; to &quot;book a call&quot; without being
              confronted with clinical intake forms or long questionnaires.
            </p>
            <ul className="text-gray-700 text-sm space-y-2 mb-4">
              <li>• Final PF/PT flow simplified to three steps:</li>
              <li className="ml-4">1) Tier / pricing + &quot;How it works&quot;.</li>
              <li className="ml-4">2) Short info form (name, email, phone, goals).</li>
              <li className="ml-4">3) Schedule a 15-minute Info Call.</li>
            </ul>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6">
                <h4 className="font-semibold text-gray-900 mb-2">Key Trust Microcopy</h4>
                <ul className="text-gray-700 text-sm space-y-2">
                  <li>• &quot;15 minutes to discuss your goals and see if we&apos;re the right fit.&quot;</li>
                  <li>• &quot;You won&apos;t be charged until after your info call.&quot;</li>
                  <li>• Clear &quot;what happens next&quot; after scheduling.</li>
                </ul>
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6">
                <h4 className="font-semibold text-gray-900 mb-2">Why It Worked</h4>
                <p className="text-body text-sm">
                  PF/PT visitors didn&apos;t need a medical-style questionnaire before trust existed. By reducing emotional
                  load and focusing on a short conversation, we turned curiosity into scheduled calls.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Embedded Payments & Operational Visibility */}
      <section className="section-padding bg-white">
        <div className="container-narrow space-y-10">
          <h2 className="heading-section text-center mb-6">Embedded Payments &amp; Operational Visibility</h2>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="card-professional p-8 bg-slate-50 border border-slate-200">
              <h3 className="heading-subsection mb-3">Embedded Payments</h3>
              <p className="text-body mb-3">
                Stripe payments were originally happening on separate hosted pages, breaking continuity and increasing
                hesitation.
              </p>
              <ul className="text-gray-700 text-sm space-y-2">
                <li>• Redesigned Review &amp; Payment step with embedded Stripe Payment Element.</li>
                <li>• Payment appears under order summary and agreements, not as a separate handoff.</li>
                <li>• Clear reinforcement of &quot;what you&apos;re buying&quot; before entering card details.</li>
              </ul>
            </div>

            <div className="card-professional p-8 bg-slate-50 border border-slate-200">
              <h3 className="heading-subsection mb-3">Admin Visibility</h3>
              <p className="text-body mb-3">
                The admin experience needed to answer a few key questions in seconds: who reached out, what they&apos;re
                interested in, and when anything is scheduled.
              </p>
              <ul className="text-gray-700 text-sm space-y-2">
                <li>• Inquiries view surfaces contact info, interest, goals, and scheduled time in one place.</li>
                <li>• &quot;Scheduled Info Call&quot; is visible at a glance for PF/PT leads.</li>
                <li>• Trials, memberships, and messages are separated into dedicated queues.</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Usability Fixes & Outcomes */}
      <section className="section-padding bg-gray-50">
        <div className="container-narrow space-y-10">
          <h2 className="heading-section text-center mb-6">Usability Fixes &amp; Outcomes</h2>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="card-professional p-8 bg-white border border-slate-200">
              <h3 className="heading-subsection mb-3">Key UX Issues Resolved</h3>
              <ul className="text-gray-700 text-sm space-y-2">
                <li>• Tap vs. expand confusion on mobile membership cards.</li>
                <li>• Inconsistent phone and DOB formats causing form errors.</li>
                <li>• Hidden mobile navigation on inner pages.</li>
                <li>• Ambiguity around whether &quot;booking&quot; was complete.</li>
                <li>• Early, clinical Acuity intake forms creating drop-off.</li>
                <li>• Safari/3DS context loss after security steps.</li>
              </ul>
            </div>

            <div className="card-professional p-8 bg-white border border-slate-200">
              <h3 className="heading-subsection mb-3">What Improved</h3>
              <ul className="text-gray-700 text-sm space-y-2">
                <li>• Phone auto-formats to (XXX) XXX-XXXX with real-time validation.</li>
                <li>• DOB auto-formats to MM/DD/YYYY and enforces 18+ requirement.</li>
                <li>• Persistent hamburger menu across public pages.</li>
                <li>• Info Call flows reduced from multi-page journeys to a simple &quot;choose option → share info → book&quot;.</li>
                <li>
                  • URL parameter fallbacks preserve tier selection even after security redirects in checkout flows.
                </li>
                <li>• Heavy, janky animations removed while preserving meaningful feedback.</li>
              </ul>
            </div>
          </div>

          <div className="card-professional p-8 bg-white border border-slate-200">
            <h3 className="heading-subsection mb-3">System-Level Outcomes</h3>
            <ul className="text-gray-800 text-sm space-y-2">
              <li>• Trial and consultation flows became lightweight, branded, and clearly guided.</li>
              <li>• Membership selection became a four-choice decision before pricing, reducing overload.</li>
              <li>
                • Checkout became progress-driven and trustworthy, with personalized waiver elements and clear legal
                content.
              </li>
              <li>• Embedded scheduling created momentum after purchase for trials, passes, and classes.</li>
              <li>
                • Admin gained a single source of truth for PF/PT inquiries, improving follow-up and pipeline
                management.
              </li>
              <li>• The UI consistently reinforced the brand promise: calm, premium, and clear.</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Navigation */}
      <section className="py-12 bg-white border-t border-gray-100">
        <div className="container-narrow">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <button
              type="button"
              onClick={() => window.history.back()}
              className="interactive-link flex items-center"
              data-testid="back-to-portfolio"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to Portfolio
            </button>

            <div className="text-center">
              <div className="text-sm text-gray-500 mb-2">Case Study: Fitness by Artizen</div>
            </div>
          </div>
        </div>
      </section>
    </CaseStudyLayout>
  );
};

export default FitnessByArtizen;
